import { createListenerMiddleware } from '@reduxjs/toolkit';
import {
  clearRuntimePermissions,
  fetchRuntimePermissions,
} from '../reducers/permissionsRuntimeState';
import { RuntimePermissionStatus } from '../models/PermissionsState';
import { fetchToken, refreshAuthToken, userLoggedOut } from '../reducers/authInfoReducer';
import { StateModel } from '../models/state.model';

let pollingTimer: ReturnType<typeof setTimeout> | null = null;

let abortController: AbortController | null = null;

const stopPolling = () => {
  if (pollingTimer) {
    clearTimeout(pollingTimer);
  }

  abortController?.abort();

  pollingTimer = null;
  abortController = null;
};

export const permissionsRuntimeListenerMiddleware = createListenerMiddleware();

const startPolling = (listenerApi: any) => {

  if (pollingTimer) {
    return;
  }

  const tick = async () => {
    abortController?.abort();

    abortController = new AbortController();

    try {
      await listenerApi.dispatch(fetchRuntimePermissions());
    } catch {
      stopPolling();
      return;
    }

    const state = listenerApi.getState();

    const hasUnavailableModules = Object.values(
      (state as StateModel).permissionsRuntime.modules,
    ).some((module) => module.status === RuntimePermissionStatus.UNAVAILABLE);

    if (!hasUnavailableModules) {
      stopPolling();
      return;
    }

    pollingTimer = setTimeout(tick, 60000);
  };

  tick();
};

/**
 * login success
 */
permissionsRuntimeListenerMiddleware.startListening({
  matcher: (action) =>
    fetchToken.fulfilled.match(action) || refreshAuthToken.fulfilled.match(action),

  effect: async (_, listenerApi) => {
    abortController?.abort();

    abortController = new AbortController();

    await listenerApi.dispatch(fetchRuntimePermissions());
  },
});

/**
 * runtime permissions loaded
 */
permissionsRuntimeListenerMiddleware.startListening({
  actionCreator: fetchRuntimePermissions.fulfilled,

  effect: async (_, listenerApi) => {
    const state = listenerApi.getState();

    const hasUnavailableModules = Object.values(
      (state as StateModel).permissionsRuntime.modules,
    ).some((module) => module.status === RuntimePermissionStatus.UNAVAILABLE);

    if (hasUnavailableModules) {
      startPolling(listenerApi);
      return;
    }

    stopPolling();
  },
});

/**
 * logout
 */
permissionsRuntimeListenerMiddleware.startListening({
  actionCreator: userLoggedOut,

  effect: async (_, listenerApi) => {
    stopPolling();

    listenerApi.dispatch(clearRuntimePermissions());
  },
});
